// src/pages/SpeakersPage.jsx
import React, { useMemo, useState } from "react";
import {
  Layout,
  Typography,
  Row,
  Col,
  Card,
  Tag,
  Space,
  Input,
  Select,
  Modal,
  Divider,
  Grid,
  Image,
} from "antd";
import Footer from "../Layouts/IcastFooter";
import IcastHeader from "../Layouts/IcastHeader";
import { SPEAKERS } from "../Components/speakersData";

const { Content } = Layout;
const { Title, Text } = Typography;
const { Meta } = Card;
const { Option } = Select;
const { useBreakpoint } = Grid;

const SpeakersPage = () => {
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [search, setSearch] = useState("");
  const [speakerTypeFilter, setSpeakerTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("featured");

  const screens = useBreakpoint();

  const titleFontSize =
    screens.xl || screens.lg ? "48px" : screens.md ? "46px" : "36px";

  const filteredSpeakers = useMemo(() => {
    let list = [...SPEAKERS];

    if (speakerTypeFilter !== "all") {
      list = list.filter((s) => s.type === speakerTypeFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.organization.toLowerCase().includes(q) ||
          (s.title || "").toLowerCase().includes(q)
      );
    }

    if (sortBy === "name-asc") {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }

    return list;
  }, [search, speakerTypeFilter, sortBy]);

  const handleCardClick = (speaker) => {
    setSelectedSpeaker(speaker);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedSpeaker(null);
  };

  return (
    <div style={{ fontFamily: "Inter, sans-serif" ,backgroundColor:"white"}}>
      <IcastHeader />

      <Title
        style={{
          textAlign: "justify",
          padding: "20px 40px",
          fontWeight: 700,
          fontSize: titleFontSize,
          backgroundColor: "#EEF0F2",
          marginBottom: 0,
        }}
      >
        ICAST Delegates
      </Title>

      <Layout style={{ minHeight: "100vh", background: "white" ,}}>
        <Content
          style={{ maxWidth: "1800px", margin: "0 auto", padding: "32px 16px" }}
        >
          {/* Header */}
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col>
              <Space direction="vertical" size={0}>
                <Title level={2} style={{ marginBottom: 0 }}>
                  Speakers
                </Title>
                <Text type="secondary">
                  {filteredSpeakers.length} speakers shown
                </Text>
              </Space>
            </Col>
            <Col>
              <Space wrap>
                <Input.Search
                  allowClear
                  placeholder="Search speakers or affiliation"
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 260 }}
                />

                <Select
                  value={speakerTypeFilter}
                  onChange={setSpeakerTypeFilter}
                  style={{ width: 140 }}
                >
                  <Option value="all">All</Option>
                  <Option value="national">National</Option>
                  <Option value="international">International</Option>
                </Select>

                <Select
                  value={sortBy}
                  onChange={setSortBy}
                  style={{ width: 140 }}
                >
                  <Option value="featured">Sort by Featured</Option>
                  <Option value="name-asc">Sort by Name (A–Z)</Option>
                </Select>
              </Space>
            </Col>
          </Row>

          <Divider />

          {/* Cards Grid */}
          <Row gutter={[24, 24]}>
            {filteredSpeakers.map((speaker) => (
              <Col key={speaker.id} xs={18} sm={12} md={12} lg={8} xl={6}>
                <Card
                  hoverable
                  bordered={true}
                  style={{ height: "100%" }}
                  onClick={() => handleCardClick(speaker)}
                  cover={
                    <Image
                      src={speaker.photo}
                      alt={speaker.name}
                      preview={false}
                      style={{
                        width: "100%",
                        height: 260,
                        objectFit: "cover",
                        objectPosition: "top",
                        borderTopLeftRadius: 6,
                        borderTopRightRadius: 6,
                      }}
                    />
                  }
                >
                  <div style={{ padding: "16px 16px 20px" }}>
                    <Meta
                      title={
                        <Text strong style={{ fontSize: 16 }}>
                          {speaker.name}
                        </Text>
                      }
                      description={
                        <Space direction="vertical" size={4}>
                          <Text>{speaker.title}</Text>
                          <Text type="secondary">
                            {speaker.organization}
                          </Text>
                          <Space size={4}>
                            <Tag>{speaker.country}</Tag>
                            <Tag
                              color={
                                speaker.type === "national" ? "blue" : "gold"
                              }
                            >
                              {speaker.type === "national"
                                ? "National"
                                : "International"}
                            </Tag>
                          </Space>
                        </Space>
                      }
                    />
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Speaker Detail Modal */}
         <Modal
  open={isModalVisible}
  onCancel={closeModal}
  footer={null}
  width={720}
  destroyOnClose
  bodyStyle={{ padding: 24 }}
>
  {selectedSpeaker && (
    <Row gutter={24}>
      
      {/* LEFT: Image */}
      <Col xs={24} sm={8}>
        <Image
          src={selectedSpeaker.photo}
          alt={selectedSpeaker.name}
          preview={false}
          style={{
            width: "100%",
            height: 260,
            objectFit: "cover",
            objectPosition: "top",
            borderRadius: 8,
          }}
        />
      </Col>

      {/* RIGHT: Content */}
      <Col xs={24} sm={16}>
        <Title level={3} style={{ marginBottom: 4 }}>
          {selectedSpeaker.name}
        </Title>

        <Text strong>{selectedSpeaker.title}</Text>
        <br />
        <Text type="secondary">{selectedSpeaker.organization}</Text>

        <Space style={{ marginTop: 12 }} wrap>
          <Tag>{selectedSpeaker.country}</Tag>
          <Tag color={selectedSpeaker.type === "national" ? "blue" : "gold"}>
            {selectedSpeaker.type === "national"
              ? "National"
              : "International"}
          </Tag>
        </Space>

        <Divider style={{ margin: "12px 0" }} />

        <Text style={{ whiteSpace: "pre-line" }}>
          {selectedSpeaker.description}
        </Text>
      </Col>

    </Row>
  )}
</Modal>
        </Content>
      </Layout>

      <Footer />
    </div>
  );
};

export default SpeakersPage;
